/**
 * Real API implementation for Locations.
 * GET /api/v3/facilities/{facilityId}/locations – list locations for a facility.
 * Do not call unless at least one facility is selected (caller must guard).
 */

import { apiClient } from '../apiClient';
import {
  normalizePaginatedResponse,
  extractListFromPayload,
} from '../utils/paginationResponseUtils';

const V3_LOCATIONS_BASE = '/api/v3';
const getFacilityLocationsPath = (facilityId) =>
  `${V3_LOCATIONS_BASE}/facilities/${facilityId}/locations`;

const LOCATIONS_MAX_LIMIT_PER_PAGE = 10;
const EMPTY_LOCATIONS_RESPONSE = {
  data: [],
  pagination: { nextCursor: undefined, totalCount: 0 },
};

function isInvalidFacilityId(facilityId) {
  return facilityId == null || String(facilityId).trim() === '';
}

/**
 * GET /api/v3/facilities/{facilityId}/locations – paginated. Max 10 items per page.
 *
 * @param {string} facilityId
 * @param {Object} params - { limit (capped at 10), cursor (page number), search, signal }
 * @returns {Promise<Object>} { data: Array, pagination: { nextCursor, totalCount } }
 */
export const getLocationsByFacilityPaginated = async (facilityId, params = {}) => {
  if (isInvalidFacilityId(facilityId)) return EMPTY_LOCATIONS_RESPONSE;

  const { limit = 10, cursor, search, signal } = params;
  const pageSize = Math.min(Number(limit) || 10, LOCATIONS_MAX_LIMIT_PER_PAGE);
  const page =
    cursor != null && cursor !== '' ? Math.max(1, Number(cursor)) : 1;
  const requestParams = { page, limit: pageSize };
  if (search) requestParams.search = search;

  const response = await apiClient.get(getFacilityLocationsPath(facilityId), {
    params: requestParams,
    signal,
  });
  const raw = response?.data ?? response;
  return normalizePaginatedResponse(raw, {
    pageSize,
    maxPageSize: LOCATIONS_MAX_LIMIT_PER_PAGE,
    sliceToPageSize: true,
  });
};

/**
 * GET /api/v3/facilities/{facilityId}/locations – list (e.g. drawer, count, duplicate modal).
 * Only called when one facility is selected.
 *
 * @param {string} facilityId
 * @param {Object} params - { search, signal }
 * @returns {Promise<Object>} { data: Array }
 */
export const getLocationsByFacility = async (facilityId, params = {}) => {
  if (isInvalidFacilityId(facilityId)) return { data: [] };

  const { search, signal } = params;
  const requestParams = { page: 1, limit: LOCATIONS_MAX_LIMIT_PER_PAGE };
  if (search) requestParams.search = search;

  const response = await apiClient.get(getFacilityLocationsPath(facilityId), {
    params: requestParams,
    signal,
  });
  const raw = response?.data ?? response;
  const data = extractListFromPayload(raw);
  return { data };
};

const getLocationPath = (locationId) => `${V3_LOCATIONS_BASE}/locations/${locationId}`;

/**
 * @param {string} locationId
 * @returns {Promise<Object>} response.data
 */
export const getLocation = async (locationId) => {
  const response = await apiClient.get(getLocationPath(locationId));
  return response.data;
};

/**
 * POST /api/v3/locations/bulk-status – check cleaning standards status for multiple locations.
 * Request: { locationIds: ["uuid-1", "uuid-2", ...] }
 * Response: { success, data: [{ locationId, locationName, facilityId, facilityName, cleaningStandardsStatus }] }
 *
 * @param {string[]} locationIds
 * @returns {Promise<{ success: boolean, data: Array }>}
 */
export const getLocationsBulkStatus = async (locationIds) => {
  if (!locationIds || locationIds.length === 0) return { success: true, data: [] };
  const { data } = await apiClient.post(`${V3_LOCATIONS_BASE}/locations/bulk-status`, {
    locationIds: locationIds.filter(Boolean),
  });
  return data;
};

/**
 * @param {string} facilityId
 * @param {Object} locationData - { name }
 * @returns {Promise<Object>} response.data
 */
export const createLocation = async (facilityId, locationData) => {
  const response = await apiClient.post(
    getFacilityLocationsPath(facilityId),
    locationData,
  );
  return response.data;
};

/**
 * @param {string} locationId
 * @param {Object} body - { newName, includeStandards }
 * @returns {Promise<Object>} response.data
 */
export const duplicateLocation = async (locationId, body) => {
  const { newName, includeStandards } = body;
  const response = await apiClient.post(
    `${getLocationPath(locationId)}/duplicate`,
    { newName, includeStandards },
  );
  return response.data;
};

/**
 * @param {{ id: string, name: string }} data
 * @returns {Promise<Object>} Updated location
 */
export const updateLocation = async (data) => {
  const { id, name } = data;
  const response = await apiClient.patch(getLocationPath(id), { name });
  return response.data;
};

/**
 * Delete a single location and its cleaning standards (cascade).
 *
 * @param {string} locationId
 * @returns {Promise<{ success: boolean, data: { message: string, deletedAt: string } }>}
 */
export const deleteLocation = async (locationId) => {
  const response = await apiClient.delete(getLocationPath(locationId));
  return response.data;
};

/**
 * @param {string[]} locationIds
 * @returns {Promise<void>}
 */
export const deleteMultipleLocations = async (locationIds) => {
  if (!locationIds || locationIds.length === 0) return;
  await apiClient.delete(`${V3_LOCATIONS_BASE}/locations/bulk`, {
    data: { locationIds },
  });
};
